import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
	buildCalculatedValues,
	getInsuranceEligibleSeats,
	formatInsuranceOffer,
	applyInsuranceChoice
} from '@helpers'
import { toastActions } from '../../../actions'
import { busSeatSelectionActions } from '../../../actions/bus-seat-selection.actions'
import { Creators as TicketSaleCreators } from '../../../ducks/ticket-sale/ticket-sale'
import TicketSaleSelectors from '../../../ducks/ticket-sale/ticket-sale.selectors'
import BusSeatSelectionSelectors from '../../../selectors/bus-seat-selection.selectors'
import { CartService } from '../../../services/cart.service'
import { getApiErrorMessage, isRequestCanceled } from '../../../services/axios.service'
import { newWindow } from './bus-seat-selection/bus-seat-selection.styles'
import { SIMPLEGRAY, DARKYELLOW, WHITE } from '../../../styles/colors.style'

const ERROR_MSG = 'Não foi possível atualizar o seguro viagem. Tente novamente.'

const BENEFITS = [
	'Preço justo',
	'Proteção pessoal para viajantes',
	'Garantia de assistência especial caso você precise'
]

/**
 * Aceite do seguro viagem no passo de pagamento (substitui a modal da seleção de poltrona). O
 * checkbox nasce marcado - as poltronas elegíveis já foram reservadas com seguro - e desmarcar
 * tira o seguro do carrinho (MuviHub), que devolve os totais recalculados para o resumo.
 */
class TravelInsuranceComponent extends React.Component {
	constructor(props) {
		super(props)

		// Atualização otimista: o checkbox reflete o clique na hora (`optimisticChecked`) e nunca fica
		// travado; volta a seguir o Redux quando o pedido do último clique termina.
		this.state = { optimisticChecked: null }
		// Sequência do último clique: só o pedido dele vale. Os anteriores são cancelados (no
		// navegador, pelo CancelToken) e, se ainda assim chegarem ao servidor, ele os ignora.
		this.latestSequence = null

		this.onToggle = onToggle.bind(this)
		this.requestInsurance = requestInsurance.bind(this)
		this.applyInsurance = applyInsurance.bind(this)
	}

	render() {
		const { selectedBusSeatsGoing, selectedBusSeatsReturn } = this.props
		const { optimisticChecked } = this.state

		// Ida, volta e cada perna de conexão: a seção só aparece se alguma delas for RODOSOFT.
		const eligibleSeats = getInsuranceEligibleSeats([
			...selectedBusSeatsGoing,
			...selectedBusSeatsReturn
		])

		if (!eligibleSeats.length) return null

		const checked =
			optimisticChecked !== null
				? optimisticChecked
				: eligibleSeats.every(seat => seat.withInsurance)

		return (
			<StyledInsuranceCard data-testid="travel-insurance">
				<div className="title">
					<ShieldIcon />
					<span>Seguro viagem</span>
				</div>
				<ul>
					{BENEFITS.map(benefit => (
						<li key={benefit}>
							<CheckIcon />
							<span>{benefit}</span>
						</li>
					))}
				</ul>
				<button
					type="button"
					className="details"
					onClick={() =>
						newWindow('/intermunicipal/seguro', 'Seguro intermunicipal', 500, 600, '')
					}>
					<InfoIcon />
					<span>Mais detalhes</span>
				</button>
				<label className="accept" htmlFor="travel-insurance-accept">
					<input
						id="travel-insurance-accept"
						type="checkbox"
						checked={checked}
						onChange={this.onToggle}
					/>
					<span className="box" aria-hidden="true">
						{checked && <CheckIcon />}
					</span>
					<span>Seguro viagem incluído</span>
				</label>
				<p className="value" data-testid="travel-insurance-value">
					Valor do seguro: {formatInsuranceOffer(eligibleSeats)} (Por passagem)
				</p>
			</StyledInsuranceCard>
		)
	}
}

// Última sequência emitida. No módulo (e não na instância) para seguir crescendo se o componente
// for remontado com o mesmo carrinho - o servidor compara com a última que aplicou.
let lastSequence = 0

/** Sequência estritamente crescente, ancorada no relógio para sobreviver a um reload da página. */
function nextSequence() {
	lastSequence = Math.max(Date.now(), lastSequence + 1)
	return lastSequence
}

function onToggle(event) {
	const withInsurance = event.target.checked
	const sequence = nextSequence()

	this.latestSequence = sequence
	this.setState({ optimisticChecked: withInsurance })
	this.props.setIsCalculatingValues(true)

	// Um novo clique cancela o pedido pendente (CartService.setInsurance é keep-latest).
	this.requestInsurance(withInsurance, sequence)
}

// Tentativas extras quando o carrinho foi alterado em paralelo (409): o pedido cancelado pode ter
// chegado ao servidor e gravado antes deste, que então precisa reler o carrinho e reaplicar.
const MAX_CONFLICT_RETRIES = 2

/**
 * Envia o clique ao carrinho. Só o pedido do último clique mexe na tela: um pedido cancelado ou
 * superado termina em silêncio. Em falha do último, desfaz o otimista - o checkbox volta ao último
 * valor que o carrinho confirmou (o do Redux).
 * @param {boolean} withInsurance O valor pedido
 * @param {number} sequence A ordem do clique
 */
async function requestInsurance(withInsurance, sequence) {
	const isLatest = () => this.latestSequence === sequence

	try {
		for (let attempt = 0; ; attempt++) {
			try {
				const response = await CartService.setInsurance(withInsurance, sequence)

				if (!isLatest()) return
				if (!response.success) throw new Error()

				this.applyInsurance(response.data, withInsurance)
				break
			} catch (error) {
				const conflict = error && error.response && error.response.status === 409

				if (!conflict || attempt >= MAX_CONFLICT_RETRIES || !isLatest()) throw error
			}
		}
	} catch (error) {
		if (isRequestCanceled(error) || !isLatest()) return

		this.props.toastError({ message: getApiErrorMessage(error) || ERROR_MSG })
	}

	this.latestSequence = null
	this.props.setIsCalculatingValues(false)
	this.setState({ optimisticChecked: null })
}

/**
 * Aplica no Redux o carrinho recalculado (poltronas + resumo do pedido).
 * @param {object} data O summary do carrinho devolvido pelo MuviHub
 * @param {boolean} withInsurance O valor gravado
 */
function applyInsurance(data, withInsurance) {
	const {
		calculatedValues,
		selectedBusSeatsGoing,
		selectedBusSeatsReturn,
		setCalculatedValues,
		updateSelectedSeats
	} = this.props

	// Poltronas do Redux: o caminho de finalização legado lê o flag e o preço de cada poltrona.
	const seatUpdates = {}
	applyInsuranceChoice(
		data,
		[...selectedBusSeatsGoing, ...selectedBusSeatsReturn],
		withInsurance
	).forEach(({ transactionId, withInsurance, insurance, price }) => {
		seatUpdates[transactionId] = { withInsurance, insurance, price }
	})
	updateSelectedSeats(seatUpdates)

	// O resumo do pedido: setCalculatedValues SUBSTITUI a slice, então o contexto de
	// remarcação é repassado (ver resolveRebookingContext).
	setCalculatedValues(
		buildCalculatedValues(
			data,
			applyInsuranceChoice(data, calculatedValues.ticketsGoing, withInsurance),
			applyInsuranceChoice(data, calculatedValues.ticketsReturn, withInsurance),
			calculatedValues.rebookingContext
		)
	)
}

const ShieldIcon = () => (
	<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
		strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
	</svg>
)

const CheckIcon = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
		strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<polyline points="20 6 9 17 4 12" />
	</svg>
)

const InfoIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
		strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<circle cx="12" cy="12" r="10" />
		<line x1="12" y1="16" x2="12" y2="12" />
		<line x1="12" y1="8" x2="12.01" y2="8" />
	</svg>
)

const StyledInsuranceCard = styled.div`
	background: ${SIMPLEGRAY};
	color: ${WHITE};
	border-radius: 2rem;
	padding: 2.4rem 2.4rem 2rem;
	margin-bottom: 2rem;
	font-size: 1.6rem;
	line-height: 1.4;

	.title {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		font-size: 2.2rem;
		font-weight: 600;
		margin-bottom: 1.2rem;
	}

	ul {
		list-style: none;
		margin: 0 0 1.2rem;
		padding: 0;

		li {
			display: flex;
			align-items: center;
			gap: 1.2rem;
			margin-bottom: 0.8rem;

			svg {
				color: ${DARKYELLOW};
				flex-shrink: 0;
			}
		}
	}

	.details {
		display: inline-flex;
		align-items: center;
		gap: 0.8rem;
		cursor: pointer;
		margin: 0 0 1.6rem;
		padding: 0;
		border: 0;
		background: none;
		color: inherit;
		font: inherit;

		&:focus-visible {
			outline: 2px solid ${WHITE};
			outline-offset: 2px;
		}

		span {
			text-decoration: underline;
		}
	}

	.accept {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		font-size: 2rem;
		font-weight: 600;
		cursor: pointer;

		input {
			position: absolute;
			opacity: 0;
			width: 0;
			height: 0;
		}

		.box {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 3.2rem;
			height: 3.2rem;
			border: 2px solid ${DARKYELLOW};
			border-radius: 0.6rem;
			color: ${DARKYELLOW};
			flex-shrink: 0;
		}

		input:focus-visible + .box {
			outline: 2px solid ${WHITE};
			outline-offset: 2px;
		}

	}

	.value {
		margin: 1rem 0 0;
	}
`

const mapStateToProps = store => ({
	calculatedValues: TicketSaleSelectors.getCalculatedValues(store),
	selectedBusSeatsGoing: BusSeatSelectionSelectors.getSelectedBusSeatsGoing(
		store.busSeatSelection
	),
	selectedBusSeatsReturn: BusSeatSelectionSelectors.getSelectedBusSeatsReturn(
		store.busSeatSelection
	)
})

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			toastError: toastActions.danger,
			setCalculatedValues: TicketSaleCreators.setCalculatedValues,
			setIsCalculatingValues: TicketSaleCreators.setIsCalculatingValues,
			updateSelectedSeats: busSeatSelectionActions.updateSelectedSeats
		},
		dispatch
	)

const TravelInsurance = connect(
	mapStateToProps,
	mapDispatchToProps
)(TravelInsuranceComponent)

export default TravelInsurance
